import { useStoryblokApi } from "@storyblok/react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Grid from "@mui/material/Grid2";
import CustomButton from "components/Button";
import { Card, CardActionArea, CardContent, CardMedia, Link, List, Typography } from "@mui/material";

const BlogCategoryPage = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { category } = useParams();
  const storyBlokApi = useStoryblokApi();
  const [allCategoryArticles, setAllCategoryArticles] = useState<any[]>([]);
  const currentLanguage = i18n.language;
  const categoriesArr = [
    "trainings",
    "cases",
    "guides",
    "trands",
    "features",
    "interviews",
    "marketing",
    "moderation",
    "technical-datails",
    "support-tools",
    "affiliates",
    "careers",
  ];

  const filtredCategories = categoriesArr.filter((categoryName) => categoryName !== category);

  const getAllCategoryStories = async () => {
    const res = await storyBlokApi.get("cdn/stories", {
      version: "draft",
      language: currentLanguage,
      starts_with: `${category}/`,
      per_page: 100,
    });

    if (!res.data.stories || res.data.stories.length === 0) {
      return;
    }

    setAllCategoryArticles(res.data.stories);
  };

  useEffect(() => {
    setAllCategoryArticles([]);
    void getAllCategoryStories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, currentLanguage]);

  return (
    <>
      <CustomButton
        width={"max-content"}
        height={30}
        textButton={t("tohomepage")}
        customBgColor="#F4701B"
        customHoverColor="#f0670b"
        customBorderRadius="16px"
        customFontSize="14px"
        customMargin="0px 0px 24px 0px"
        customPadding="8px 24px"
        customTextTransform="none"
        onClick={() => navigate(`/blog/${currentLanguage}`)}
      />
      <Grid container spacing={4}>
        <Grid size={{ xs: 12, lg: 9 }}>
          <Typography
            component="h1"
            sx={{
              marginBottom: "24px",
              color: "#2E263D",
              fontWeight: 500,
              fontSize: "24px",
              lineHeight: 1.24,
            }}
          >
            {t(category!)}
          </Typography>

          <Grid container component="ul" columnSpacing={3} rowSpacing={5}>
            {allCategoryArticles.map((article: any) => {
              const articleTitle = article?.content?.main_title;
              const articlePreviewText = article?.content?.preview_text;
              const articlePreviewImage = article?.content?.preview_image?.filename;
              const articlePreviewImageAlt = article?.content?.preview_image?.alt || "";
              const fullSlug = article?.full_slug;
              const articleKey = article?.uuid;

              return (
                <Grid component="li" key={articleKey} size={{ xs: 12, sm: 6 }}>
                  <Card sx={{ height: "100%", borderRadius: "24px", boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)" }}>
                    <CardActionArea
                      onClick={() =>
                        navigate(
                          currentLanguage === "en" ? `/blog/${fullSlug}` : `/blog/${currentLanguage}/${fullSlug}`
                        )
                      }
                    >
                      <CardMedia
                        component="img"
                        height="220"
                        image={articlePreviewImage}
                        alt={articlePreviewImageAlt}
                      />
                      <CardContent>
                        <Typography
                          gutterBottom
                          component="h3"
                          sx={{ color: "#2E263D", fontSize: "18px", fontWeight: 600, lineHeight: "24px" }}
                        >
                          {articleTitle}
                        </Typography>
                        <Typography component="p" sx={{ color: "#2E263D", fontSize: "16px", lineHeight: "24px" }}>
                          {articlePreviewText}
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Grid>
        <Grid size={{ xs: 12, lg: 3 }}>
          <Typography
            component="h2"
            sx={{
              marginBottom: "18px",
              color: "#2E263D",
              fontWeight: 500,
              fontSize: "24px",
              lineHeight: 1.24,
            }}
          >
            {t("moreCategories")}
          </Typography>
          <List dense={false} sx={{ display: "flex", flexDirection: "column", gap: "10px", padding: "0px" }}>
            {filtredCategories.map((categoryName) => {
              return (
                <Link
                  key={categoryName}
                  component="li"
                  underline="none"
                  sx={{
                    justifyContent: "flex-start",
                    color: "#000",
                    fontSize: "16px",
                    lineHeight: "24px",
                    transition: "color 0.3s ease",
                    cursor: "pointer",
                    "&:hover": { color: "#F4701B" },

                    position: "relative",
                    paddingLeft: "24px",

                    "&::before": {
                      content: "''",
                      position: "absolute",
                      left: 0,
                      top: 0,
                      transform: "translateY(100%)",
                      width: "8px",
                      height: "8px",
                      backgroundColor: "#F4701B",
                      borderRadius: "50%",
                    },
                  }}
                  onClick={() => {
                    navigate(`/blog/${currentLanguage}/${categoryName}`);
                  }}
                >
                  {t(categoryName)}
                </Link>
              );
            })}
          </List>
        </Grid>
      </Grid>
    </>
  );
};

export default BlogCategoryPage;
